import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../api/client'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Coins, AlertTriangle, Clock, ArrowRight } from 'lucide-react'
import { formatNumber, formatDateTime, getDaysUntilExpiry, getExpiryStatus } from '../lib/utils'
import type { PointBalance, PointHistory } from '../types'

export function PointsPage() {
  const { t } = useTranslation()

  const [balance, setBalance] = useState<PointBalance | null>(null)
  const [history, setHistory] = useState<PointHistory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const LIMIT = 20

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [balanceData, historyData] = await Promise.all([
        apiClient.getPointBalance(),
        apiClient.getPointHistory(LIMIT, 0),
      ])
      setBalance(balanceData)
      setHistory(historyData.history)
      setHasMore(historyData.history.length === LIMIT)
    } catch (error) {
      console.error('Failed to fetch points data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMore = async () => {
    try {
      const offset = (page + 1) * LIMIT
      const historyData = await apiClient.getPointHistory(LIMIT, offset)
      setHistory((prev) => [...prev, ...historyData.history])
      setHasMore(historyData.history.length === LIMIT)
      setPage((prev) => prev + 1)
    } catch (error) {
      console.error('Failed to load more history:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('points.title')}</h1>
          <p className="text-light-text-muted dark:text-dark-text-muted mt-1">
            포인트 잔액 및 사용 내역을 확인하세요
          </p>
        </div>
        <Link to="/payments" className="btn btn-primary inline-flex items-center gap-2">
          <Coins className="w-4 h-4" />
          {t('nav.payments')}
        </Link>
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-r from-primary to-primary-hover text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm">{t('points.totalPoints')}</p>
            <p className="text-4xl font-bold mt-2">
              {formatNumber(balance?.total_points || 0)}
              <span className="text-2xl ml-1">P</span>
            </p>
          </div>
          <Coins className="w-16 h-16 text-white/30" />
        </div>
      </Card>

      {/* Points by Plan */}
      {balance?.points_by_plan && balance.points_by_plan.length > 0 && (
        <Card title={t('points.pointsByPlan')}>
          <div className="space-y-4">
            {balance.points_by_plan.map((plan, index) => {
              const daysUntil = getDaysUntilExpiry(plan.expires_at)
              const status = getExpiryStatus(daysUntil)

              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Coins className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium capitalize">{plan.plan_type} 플랜</p>
                      <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                        {formatNumber(plan.points_remaining)}P
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    {status === 'expired' ? (
                      <Badge variant="danger">{t('points.expired')}</Badge>
                    ) : status === 'expiring-soon' ? (
                      <Badge variant="warning" className="inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {daysUntil}{t('points.days')}
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-1 text-sm text-light-text-muted dark:text-dark-text-muted">
                        <Clock className="w-4 h-4" />
                        {daysUntil}{t('points.days')}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* History */}
      <Card title={t('points.history')}>
        {history.length === 0 ? (
          <div className="text-center py-8 text-light-text-muted dark:text-dark-text-muted">
            <Coins className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('points.noHistory')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-light-border dark:border-dark-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('points.date')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('points.type')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('points.description')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('points.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr
                      key={item.transactionId}
                      className="border-b border-light-border dark:border-dark-border last:border-0"
                    >
                      <td className="py-3 px-4 text-sm">
                        {formatDateTime(item.createdAt)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={
                            item.type === 'charge'
                              ? 'success'
                              : item.type === 'refund'
                              ? 'info'
                              : 'default'
                          }
                        >
                          {item.type === 'charge'
                            ? t('points.charge')
                            : item.type === 'refund'
                            ? t('points.refund')
                            : t('points.use')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {item.reason}
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-medium">
                        <span
                          className={
                            item.type === 'use'
                              ? 'text-danger'
                              : 'text-success'
                          }
                        >
                          {item.type === 'use' ? '-' : '+'}
                          {formatNumber(item.amount)}P
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMore}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  더 보기 <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
